import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import firebase from '../../Firebase/Firebase';
import { toDate } from '../../Utils/date';
import { createProjectDocumentPdf } from '../../Services/marketplacePayments';
import { useI18n } from '../../i18n/I18nProvider';

const toNumber = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCurrency = (value, locale = 'fr-FR') => new Intl.NumberFormat(locale, {
  style: 'currency',
  currency: 'EUR',
}).format(toNumber(value));

const personName = (...parts) => parts.filter(Boolean).join(' ').trim();
const formatLocalizedDate = (value, locale, fallback) => {
  const date = toDate(value);
  return date ? new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(date) : fallback;
};
const ACCEPTED_STATUSES = new Set(['assigned', 'completion_pending', 'completion_disputed', 'finished', 'paid', 'reviewed']);

const PartyBlock = ({ L, fallback, label, name, businessName, phone, email, address, registration, website, vatNumber }) => (
  <Party>
    <PartyLabel>{label}</PartyLabel>
    <PartyName>{businessName || name || fallback}</PartyName>
    {businessName && name && <PartySubline>{L('Représenté par', 'Represented by')} {name}</PartySubline>}
    <PartyLine><span>{L('Adresse', 'Address')}</span><strong>{address || fallback}</strong></PartyLine>
    <PartyLine><span>{L('Téléphone', 'Phone')}</span><strong>{phone || fallback}</strong></PartyLine>
    <PartyLine><span>{L('E-mail', 'Email')}</span><strong>{email || fallback}</strong></PartyLine>
    {registration && <PartyLine><span>{L('SIRET / immatriculation', 'SIRET / registration')}</span><strong>{registration}</strong></PartyLine>}
    {vatNumber && <PartyLine><span>{L('TVA', 'VAT')}</span><strong>{vatNumber}</strong></PartyLine>}
    {website && <PartyLine><span>{L('Site web', 'Website')}</span><strong>{website}</strong></PartyLine>}
  </Party>
);

const EmptyDocument = ({ loading, error }) => {
  const history = useHistory();
  const { language } = useI18n();
  const en = language === 'en';
  const L = (fr, english) => (en ? english : fr);
  return (
    <EmptyState>
      <h1>{loading ? L('Chargement du document…', 'Loading document…') : L('Document indisponible', 'Document unavailable')}</h1>
      <p>{loading ? L('Les informations du projet sont en cours de récupération.', 'Project information is being retrieved.') : error || L('Ouvrez le devis ou le contrat depuis la page du projet.', 'Open the quote or contract from the project page.')}</p>
      {!loading && <button type="button" onClick={() => history.goBack()}>{L('Retour aux projets', 'Back to projects')}</button>}
    </EmptyState>
  );
};

const PrintableProjectDocument = ({ type }) => {
  const { language, locale } = useI18n();
  const en = language === 'en';
  const L = (fr, english) => (en ? english : fr);
  const fallback = L('Non renseigné', 'Not provided');
  const history = useHistory();
  const location = useLocation();
  const initialDemand = location.state?.demandToPrint || null;
  const queryDemandId = new URLSearchParams(location.search).get('demandId') || initialDemand?.demand_id || '';
  const [demand, setDemand] = useState(initialDemand);
  const [loading, setLoading] = useState(!initialDemand && Boolean(queryDemandId));
  const [loadError, setLoadError] = useState('');
  const [generating, setGenerating] = useState(false);
  const isContract = type === 'contract';

  useEffect(() => {
    if (initialDemand || !queryDemandId) return undefined;
    let active = true;
    setLoading(true);
    firebase.firestore().collection('demands').doc(queryDemandId).get()
      .then((snapshot) => {
        if (!active) return;
        if (!snapshot.exists) throw new Error(L('Ce projet est introuvable.', 'This project could not be found.'));
        setDemand({ demand_id: snapshot.id, ...snapshot.data() });
      })
      .catch((error) => {
        if (active) setLoadError(error?.message || L('Le document n’a pas pu être chargé.', 'The document could not be loaded.'));
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [initialDemand, queryDemandId, en]);

  const materials = useMemo(
    () => (Array.isArray(demand?.contract?.material_list) ? demand.contract.material_list : []),
    [demand],
  );

  const documentTitle = `${isContract ? (en ? 'contract' : 'contrat') : (en ? 'quote' : 'devis')}-${demand?.demand_id || 'lepetitboulot'}`;

  if (!demand?.contract) return <EmptyDocument loading={loading} error={loadError} />;

  const contract = demand.contract;
  const computedMaterialTotal = materials.reduce(
    (sum, item) => sum + toNumber(item.material_price) * toNumber(item.material_quantity),
    0,
  );
  const storedMaterialTotal = Number(contract.material_cost);
  const wage = toNumber(contract.wage);
  const travelCost = toNumber(contract.travel_cost);
  const storedSubtotal = Number(contract.subtotal_ht);
  const storedVat = Number(contract.vat_amount);
  const storedTotal = Number(contract.total_cost);
  const materialTotal = Number.isFinite(storedMaterialTotal) ? storedMaterialTotal : computedMaterialTotal;
  const subtotal = Number.isFinite(storedSubtotal) ? storedSubtotal : materialTotal + wage + travelCost;
  const vatAmount = Number.isFinite(storedVat) ? storedVat : 0;
  const total = Number.isFinite(storedTotal) ? storedTotal : subtotal + vatAmount;
  const platformService = toNumber(contract.platform_service_gross || demand.pricing?.platform_service_gross);
  const clientSurcharge = 0;
  const accepted = ACCEPTED_STATUSES.has(demand.demand_status);
  const issueDate = contract.post_date || demand.updated_at || demand.date;
  const acceptanceDate = demand.contract_accepted_at || demand.payment_provider?.ready_at || null;
  const depositRate = Number(demand.payment_plan?.deposit_rate ?? 0.2);
  const validityDays = Number(contract.quote_validity_days || 30);

  const base64ToFile = (base64, filename) => {
    const binary = window.atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
    return new File([bytes], filename, { type: 'application/pdf' });
  };

  const generateOfficialPdf = async () => {
    const result = await createProjectDocumentPdf({
      demandId: demand.demand_id,
      type: isContract ? 'contract' : 'quote',
      language,
    });
    if (!result?.base64) throw new Error(L('Le serveur a renvoyé un PDF vide.', 'The server returned an empty PDF.'));
    const filename = result.filename || `${documentTitle}.pdf`;
    return base64ToFile(result.base64, filename);
  };

  const openOfficialPdf = async () => {
    if (generating) return;
    const popup = window.open('', '_blank');
    if (popup) {
      popup.document.write(`<!doctype html><title>${L('Préparation du PDF', 'Preparing PDF')}</title><body style="font-family:Arial;padding:2rem">${L('Préparation du document officiel…', 'Preparing the official document…')}</body>`);
    }
    setGenerating(true);
    try {
      const file = await generateOfficialPdf();
      const objectUrl = URL.createObjectURL(file);
      if (popup) popup.location.replace(objectUrl);
      else window.location.assign(objectUrl);
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 5 * 60 * 1000);
    } catch (error) {
      if (popup) popup.close();
      toast.error(error?.message || L('Le PDF n’a pas pu être ouvert.', 'The PDF could not be opened.'));
    } finally {
      setGenerating(false);
    }
  };

  const saveOfficialPdf = async () => {
    if (generating) return;
    setGenerating(true);
    try {
      const file = await generateOfficialPdf();
      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: file.name,
          text: `${isContract ? L('Contrat', 'Contract') : L('Devis', 'Quote')} LePetitBoulot`,
        });
        return;
      }

      const objectUrl = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = file.name;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
      toast.success(L('Le PDF officiel a été enregistré.', 'The official PDF was saved.'));
    } catch (error) {
      if (error?.name !== 'AbortError') toast.error(error?.message || L('Le PDF n’a pas pu être enregistré.', 'The PDF could not be saved.'));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Page>
      <Toolbar aria-label={L("Actions du document", "Document actions")}>
        <BackButton type="button" onClick={() => history.goBack()}>{L("Retour", "Back")}</BackButton>
        <ToolbarGroup>
          <PrintButton type="button" onClick={openOfficialPdf} disabled={generating}>
            {generating ? L('Préparation…', 'Preparing…') : L('Ouvrir / imprimer le PDF', 'Open / print PDF')}
          </PrintButton>
          <DownloadButton type="button" onClick={saveOfficialPdf} disabled={generating}>
            {generating ? L('Création du PDF…', 'Creating PDF…') : L('Enregistrer / partager', 'Save / share')}
          </DownloadButton>
        </ToolbarGroup>
      </Toolbar>
      <DocumentHelp>{L("Le PDF officiel est généré côté serveur au format A4 vectoriel. Sur téléphone, « Enregistrer / partager » ouvre la feuille de partage pour l’ajouter à Fichiers, l’envoyer ou l’imprimer.", "The official PDF is generated securely on the server in vector A4 format. On mobile, “Save / share” opens the share sheet to save, send or print it.")}</DocumentHelp>

      <Document>
        <DocumentHeader>
          <BrandBlock>
            {contract.artisan_company_logo ? (
              <CompanyLogo src={contract.artisan_company_logo} alt={L(`Logo de ${contract.artisan_business_name || 'l’entreprise artisanale'}`, `Logo of ${contract.artisan_business_name || 'the artisan business'}`)} />
            ) : (
              <BrandMark aria-hidden="true"><span>P</span><span>B</span></BrandMark>
            )}
            <BrandCopy>
              <Brand>{contract.artisan_business_name || 'LE PETIT BOULOT'}</Brand>
              <BrandSubtitle>
                {contract.artisan_business_name
                  ? [contract.artisan_legal_form, contract.artisan_siret ? `SIRET ${contract.artisan_siret}` : ''].filter(Boolean).join(' · ') || L('Prestataire artisan', 'Artisan service provider')
                  : L('Mise en relation et suivi de prestations artisanales', 'Artisan matching and project tracking')}
              </BrandSubtitle>
              {contract.artisan_business_name && <PlatformByline>{L("Document suivi via Le Petit Boulot", "Document managed through Le Petit Boulot")}</PlatformByline>}
            </BrandCopy>
          </BrandBlock>
          <DocumentIdentity>
            <DocumentType>{isContract ? L('CONTRAT DE PRESTATION', 'SERVICE CONTRACT') : L('DEVIS', 'QUOTE')}</DocumentType>
            <Reference>{L("Réf.", "Ref.")} {demand.demand_id || '—'}</Reference>
            <Reference>{L("Émis le", "Issued on")} {formatLocalizedDate(issueDate, locale, '—')}</Reference>
          </DocumentIdentity>
        </DocumentHeader>

        <StatusBanner $accepted={accepted}>
          <strong>{accepted ? L('Devis accepté — engagement contractuel enregistré', 'Quote accepted — contractual commitment recorded') : isContract ? L('Projet de contrat à vérifier', 'Draft contract to review') : L('Offre soumise à acceptation', 'Offer awaiting acceptance')}</strong>
          <span>
            {accepted
              ? L(`Acceptation enregistrée${acceptanceDate ? ` le ${formatLocalizedDate(acceptanceDate, locale, '')}` : ''}.`, `Acceptance recorded${acceptanceDate ? ` on ${formatLocalizedDate(acceptanceDate, locale, '')}` : ''}.`)
              : L(`Offre valable ${validityDays} jours à compter de sa date d’émission.`, `Offer valid for ${validityDays} days from the issue date.`)}
          </span>
        </StatusBanner>

        <Parties>
          <PartyBlock
            L={L}
            fallback={fallback}
            label={L("Prestataire / Artisan", "Service provider / Artisan")}
            name={personName(contract.artisan_firstname, contract.artisan_lastname) || fallback}
            businessName={contract.artisan_business_name}
            phone={contract.artisan_phone_number}
            email={contract.artisan_email}
            address={contract.artisan_address}
            registration={contract.artisan_siret}
            vatNumber={contract.artisan_vat_number}
            website={contract.artisan_website}
          />
          <PartyBlock
            L={L}
            fallback={fallback}
            label={L("Client", "Client")}
            name={personName(demand.user_firstname, demand.user_lastname) || fallback}
            phone={demand.client_phone_number}
            email={demand.user_email_address}
            address={demand.client_address || demand.demand_address}
          />
        </Parties>

        <Section>
          <SectionTitle>{L("1. Objet et lieu d’exécution", "1. Scope and place of performance")}</SectionTitle>
          <InfoGrid>
            <InfoItem $wide><span>{L("Nature de la demande", "Client request")}</span><strong>{demand.description || fallback}</strong></InfoItem>
            <InfoItem $wide><span>{L("Prestation proposée", "Proposed service")}</span><strong>{contract.description || fallback}</strong></InfoItem>
            <InfoItem><span>{L("Adresse du chantier", "Job-site address")}</span><strong>{demand.demand_address || fallback}</strong></InfoItem>
            <InfoItem><span>{L("Catégorie", "Category")}</span><strong>{demand.demand_type || fallback}</strong></InfoItem>
          </InfoGrid>
        </Section>

        <Section>
          <SectionTitle>{L("2. Planning prévisionnel", "2. Estimated schedule")}</SectionTitle>
          <InfoGrid>
            <InfoItem><span>{L("Date de début", "Start date")}</span><strong>{formatLocalizedDate(contract.start_date, locale, L("À convenir", "To be agreed"))}</strong></InfoItem>
            <InfoItem><span>{L("Durée estimée", "Estimated duration")}</span><strong>{contract.estimated_duration || L("À préciser entre les parties", "To be agreed by the parties")}</strong></InfoItem>
            <InfoItem><span>{L("Fin déclarée / prévue", "Declared / planned end")}</span><strong>{formatLocalizedDate(contract.end_date, locale, L("Non renseignée", "Not provided"))}</strong></InfoItem>
            <InfoItem><span>{L("Validité de l’offre", "Offer validity")}</span><strong>{validityDays} {L("jours", "days")}</strong></InfoItem>
          </InfoGrid>
        </Section>

        <Section>
          <SectionTitle>{L("3. Détail des prix", "3. Price details")}</SectionTitle>
          <MobileTableHint>{L("Sur téléphone, chaque ligne est présentée sous forme de fiche pour rester lisible.", "On mobile, each row is displayed as a card for readability.")}</MobileTableHint>
          <CostTableWrapper>
            <CostTable>
              <thead>
                <tr>
                  <th>{L("Désignation", "Description")}</th>
                  <th>{L("Quantité", "Quantity")}</th>
                  <th>{L("Prix unitaire", "Unit price")}</th>
                  <th>{L("Total", "Total")}</th>
                </tr>
              </thead>
              <tbody>
                {materials.length ? materials.map((item, index) => {
                  const quantity = toNumber(item.material_quantity);
                  const unitPrice = toNumber(item.material_price);
                  return (
                    <tr key={`${item.material_name || 'materiel'}-${index}`}>
                      <td data-label={L('Désignation', 'Description')}>{item.material_name || L('Matériel', 'Material')}</td>
                      <td data-label={L("Quantité", "Quantity")}>{quantity || '—'}</td>
                      <td data-label={L("Prix unitaire", "Unit price")}>{formatCurrency(unitPrice, locale)}</td>
                      <td data-label={L("Total", "Total")}>{formatCurrency(quantity * unitPrice, locale)}</td>
                    </tr>
                  );
                }) : (
                  <tr>
                    <td data-label={L("Désignation", "Description")}>{L("Matériaux", "Materials")}</td>
                    <td data-label={L("Quantité", "Quantity")}>—</td>
                    <td data-label={L("Prix unitaire", "Unit price")}>—</td>
                    <td data-label={L("Total", "Total")}>{formatCurrency(materialTotal, locale)}</td>
                  </tr>
                )}
                <tr>
                  <td data-label={L("Désignation", "Description")}>{L("Main-d’œuvre", "Labour")}</td>
                  <td data-label={L("Quantité", "Quantity")}>
                    {contract.labour_mode === 'hours' && toNumber(contract.labour_hours) > 0 ? `${toNumber(contract.labour_hours)} h` : 1}
                  </td>
                  <td data-label={L("Prix unitaire", "Unit price")}>
                    {contract.labour_mode === 'hours' && toNumber(contract.labour_hours) > 0
                      ? formatCurrency(toNumber(contract.hourly_rate), locale)
                      : formatCurrency(wage, locale)}
                  </td>
                  <td data-label={L("Total", "Total")}>{formatCurrency(wage, locale)}</td>
                </tr>
                {travelCost > 0 && (
                  <tr>
                    <td data-label={L("Désignation", "Description")}>{L("Frais de déplacement", "Travel")}</td>
                    <td data-label="Quantité">1</td>
                    <td data-label={L("Prix unitaire", "Unit price")}>{formatCurrency(travelCost, locale)}</td>
                    <td data-label={L("Total", "Total")}>{formatCurrency(travelCost, locale)}</td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr><td colSpan="3">{L("Sous-total", "Subtotal")}</td><td data-label={L("Sous-total", "Subtotal")}>{formatCurrency(subtotal, locale)}</td></tr>
                {(vatAmount > 0 || contract.tax_label) && (
                  <tr><td colSpan="3">{contract.tax_label || `TVA ${toNumber(contract.vat_rate)} %`}</td><td data-label={L("TVA", "VAT")}>{formatCurrency(vatAmount, locale)}</td></tr>
                )}
                {platformService > 0 && (
                  <tr className="included-service">
                    <td colSpan="3">{L("Dont gestion du projet et sécurisation du paiement LPB — supportées par l’artisan", "Of which LPB project management and payment protection — paid by the artisan")}</td>
                    <td data-label={L("Inclus", "Included")}>{formatCurrency(platformService, locale)}</td>
                  </tr>
                )}
                <tr className="client-surcharge">
                  <td colSpan="3">{L("Supplément facturé au client", "Additional charge to the client")}</td>
                  <td data-label={L("Supplément", "Additional charge")}>{formatCurrency(clientSurcharge, locale)}</td>
                </tr>
                <tr className="grand-total"><td colSpan="3">{L("TOTAL À PAYER", "TOTAL DUE")}</td><td data-label={L("Total à payer", "Total due")}>{formatCurrency(total, locale)}</td></tr>
              </tfoot>
            </CostTable>
          </CostTableWrapper>
        </Section>

        <Section>
          <SectionTitle>{L("4. Paiement et exécution", "4. Payment and performance")}</SectionTitle>
          <TermsGrid>
            <Term><strong>{L("Acompte", "Deposit")}</strong><span>{Math.round(depositRate * 100)}% {L("à l’acceptation du devis.", "when the quote is accepted.")}</span></Term>
            <Term><strong>{L("Solde", "Final balance")}</strong><span>{L("Après confirmation de la fin des travaux par le client.", "After the client confirms completion.")}</span></Term>
            <Term><strong>{L("Traitement bancaire sécurisé", "Secure bank processing")}</strong><span>{L("Les frais de traitement bancaire sont supportés par l’artisan et ne s’ajoutent pas au total client.", "Bank processing fees are paid by the artisan and are not added to the client total.")}</span></Term>
            <Term><strong>{L("Rôle de la plateforme", "Platform role")}</strong><span>{L("Le Petit Boulot facilite la mise en relation, le suivi et le paiement ; l’artisan reste le prestataire de la prestation.", "Le Petit Boulot facilitates matching, project tracking and payment; the artisan remains the service provider.")}</span></Term>
          </TermsGrid>
          {contract.payment_terms && <LegalLine><strong>{L("Conditions particulières de paiement :", "Specific payment terms:")}</strong> {contract.payment_terms}</LegalLine>}
          {contract.artisan_insurance && <LegalLine><strong>{L("Assurance professionnelle :", "Professional insurance:")}</strong> {contract.artisan_insurance}</LegalLine>}
          {contract.tax_label && <LegalLine><strong>{L("Régime fiscal indiqué par l’artisan :", "Tax treatment stated by the artisan:")}</strong> {contract.tax_label}</LegalLine>}
        </Section>

        <ElectronicInvoiceNotice>
          <strong>{L('Facturation électronique', 'Electronic invoicing')}</strong>
          <span>{L(
            'Ce devis ou contrat n’est pas une facture électronique. Lorsque la facture relève de la réforme française, son émission, sa transmission et, selon le cas, le e-reporting doivent passer par une plateforme agréée. Le PDF reste une copie lisible du dossier.',
            'This quote or contract is not an electronic invoice. Where the French reform applies, invoice issuance, transmission and, where applicable, e-reporting must go through an accredited platform. The PDF remains a readable copy of the project record.',
          )}</span>
        </ElectronicInvoiceNotice>

        <Acceptance>
          <AcceptanceText>
            <strong>{L("Acceptation des conditions", "Acceptance of terms")}</strong>
            <p>
              {L("Le document accepté est conservé dans l’historique du projet. Les parties doivent vérifier les informations professionnelles, fiscales, d’assurance et les conditions particulières applicables à la prestation avant l’exécution.", "The accepted document is kept in the project history. The parties must check business, tax and insurance information and any specific terms before work starts.")}
            </p>
          </AcceptanceText>
          <SignatureGrid>
            <SignatureBox>
              <span>{L("Client — Bon pour accord", "Client — Approved")}</span>
              <div>
                {demand.contract_acceptance?.evidence_id
                  ? <>{L('Acceptation électronique horodatée (niveau simple)', 'Time-stamped electronic acceptance (basic level)')}<br />{L('Preuve :', 'Evidence:')} {demand.contract_acceptance.evidence_id}</>
                  : accepted ? L('Acceptation historique enregistrée', 'Historical acceptance recorded') : L('Date et signature', 'Date and signature')}
              </div>
            </SignatureBox>
            <SignatureBox><span>{L("Artisan — Émission du devis", "Artisan — Quote issue")}</span><div>{accepted ? L("Devis transmis depuis le compte artisan authentifié", "Quote sent from the authenticated artisan account") : L("Date et signature", "Date and signature")}</div></SignatureBox>
          </SignatureGrid>
          <EvidenceDisclosure>
            {L("L’acceptation enregistrée par LePetitBoulot est une acceptation électronique simple liée au compte authentifié, à un horodatage et à l’empreinte des données contractuelles. Elle n’est pas présentée comme une signature électronique qualifiée.", "The acceptance recorded by LePetitBoulot is a basic electronic acceptance linked to the authenticated account, a timestamp and a fingerprint of the contractual data. It is not presented as a qualified electronic signature.")}
          </EvidenceDisclosure>
        </Acceptance>

        {contract.document_footer && <DocumentFooterNote>{contract.document_footer}</DocumentFooterNote>}
        <Footer>
          <strong>Le Petit Boulot</strong>
          <span>73 rue Rabaneau, 33440 Ambarès-et-Lagrave</span>
          <span>support@lepetitboulot.fr</span>
          <span>{L("Document généré le", "Document generated on")} {new Date().toLocaleDateString(locale)}</span>
        </Footer>
      </Document>
    </Page>
  );
};

const Page = styled.main`
  min-height: 100vh;
  width: 100%;
  overflow-x: hidden;
  padding: 2rem 1rem 6rem;
  background: #eef1f3;
  color: #15202a;
  @media (max-width: 640px) { padding: 0.75rem 0.55rem 5rem; }
  @media print { padding: 0; background: #fff; }
`;
const Toolbar = styled.div`
  width: min(210mm, 100%); margin: 0 auto 1rem; display:flex; align-items:center; justify-content:space-between; gap:.75rem;
  @media(max-width:640px){align-items:stretch;flex-direction:column;}
  @media print { display:none !important; }
`;
const ToolbarGroup = styled.div`display:flex;gap:.55rem;@media(max-width:640px){display:grid;grid-template-columns:1fr;}`;
const DocumentHelp = styled.p`width:min(210mm,100%);margin:-.35rem auto .85rem;color:#66727d;font-size:.68rem;line-height:1.45;@media print{display:none;}@media(max-width:640px){font-size:.64rem;}`;
const ToolbarButton = styled.button`
  min-height:2.85rem;padding:.72rem 1rem;border-radius:999px;font:inherit;font-weight:850;cursor:pointer;box-sizing:border-box;
  &:disabled{opacity:.6;cursor:wait;}@media(max-width:640px){width:100%;}
`;
const BackButton = styled(ToolbarButton)`border:1px solid #d8d1c6;background:#fff;color:#111;`;
const PrintButton = styled(ToolbarButton)`border:1px solid #111;background:#fff;color:#111;`;
const DownloadButton = styled(ToolbarButton)`border:1px solid #c0a375;background:#c0a375;color:#111;`;
const Document = styled.article`
  width:100%;max-width:210mm;min-height:0;margin:0 auto;padding:18mm 16mm;box-sizing:border-box;background:#fff;color:#17212b;
  border:1px solid #dfe4e8;box-shadow:0 24px 70px rgba(16,24,32,.12);font-family:Arial,Helvetica,sans-serif;
  -webkit-print-color-adjust:exact;print-color-adjust:exact;
  @media(max-width:700px){max-width:none;min-height:0;margin:0;padding:1rem;border-radius:.9rem;box-shadow:0 .5rem 1.6rem rgba(16,24,32,.08);font-size:14px;}
  @media print{width:auto;max-width:none;min-height:0;margin:0;padding:12mm;border:0;box-shadow:none;border-radius:0;}
  @page{size:A4 portrait;margin:0;}
`;
const DocumentHeader = styled.header`display:flex;align-items:flex-start;justify-content:space-between;gap:1rem;min-width:0;padding-bottom:1rem;border-bottom:3px solid #c0a375;@media(max-width:560px){display:grid;}`;
const BrandBlock = styled.div`display:flex;align-items:center;gap:.7rem;min-width:0;max-width:60%;@media(max-width:560px){max-width:100%;}`;
const BrandMark = styled.div`position:relative;width:3.2rem;height:3.2rem;flex:0 0 auto;border-radius:.82rem;background:#0b0b0b;color:#fff;font-weight:950;box-shadow:inset 0 0 0 1px rgba(255,255,255,.08);span{position:absolute;font-size:1.5rem;line-height:1;}span:first-child{left:.55rem;top:.42rem;}span:last-child{right:.5rem;bottom:.38rem;color:#c0a375;}`;
const CompanyLogo = styled.img`width:4.5rem;height:3.2rem;flex:0 0 auto;object-fit:contain;border-radius:.55rem;background:#fff;padding:.2rem;box-sizing:border-box;`;
const BrandCopy = styled.div`min-width:0;display:grid;gap:.08rem;`;
const Brand = styled.div`min-width:0;font-size:.95rem;font-weight:950;letter-spacing:.08em;overflow-wrap:anywhere;word-break:break-word;`;

const BrandSubtitle = styled.div`max-width:20rem;margin-top:.15rem;color:#66727d;font-size:.7rem;line-height:1.35;overflow-wrap:anywhere;`;
const PlatformByline = styled.div`margin-top:.15rem;color:#8b6b38;font-size:.6rem;font-weight:850;letter-spacing:.03em;`;

const DocumentIdentity = styled.div`min-width:0;display:grid;justify-items:end;gap:.18rem;text-align:right;@media(max-width:560px){justify-items:start;text-align:left;}`;
const DocumentType = styled.h1`max-width:100%;margin:0;color:#111;font-size:1.35rem;line-height:1.15;letter-spacing:.035em;overflow-wrap:anywhere;word-break:break-word;`;
const Reference = styled.span`color:#66727d;font-size:.72rem;overflow-wrap:anywhere;`;
const StatusBanner = styled.div`
  display:grid;gap:.18rem;margin-top:1rem;padding:.8rem 1rem;border-left:4px solid ${({$accepted})=>$accepted?'#2f8a58':'#c0a375'};background:${({$accepted})=>$accepted?'#edf8f1':'#fbf6ed'};
  strong{font-size:.82rem;}span{color:#596772;font-size:.74rem;line-height:1.45;}
`;
const Parties = styled.section`display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.8rem;margin-top:1rem;@media(max-width:620px){grid-template-columns:1fr;}`;
const Party = styled.div`padding:.85rem;border:1px solid #dfe4e8;border-radius:.65rem;break-inside:avoid;`;
const PartyLabel = styled.div`margin-bottom:.45rem;color:#8b6b38;font-size:.66rem;font-weight:900;letter-spacing:.1em;text-transform:uppercase;`;
const PartyName = styled.div`font-size:.94rem;font-weight:900;`;
const PartySubline = styled.div`margin-top:.12rem;color:#687681;font-size:.72rem;`;
const PartyLine = styled.div`display:grid;grid-template-columns:6rem 1fr;gap:.5rem;margin-top:.4rem;font-size:.72rem;span{color:#687681;}strong{font-weight:650;overflow-wrap:anywhere;}@media(max-width:390px){grid-template-columns:1fr;gap:.08rem;}`;
const Section = styled.section`min-width:0;margin-top:1.2rem;break-inside:auto;overflow-wrap:anywhere;`;
const SectionTitle = styled.h2`margin:0 0 .6rem;padding-bottom:.35rem;border-bottom:1px solid #dfe4e8;color:#17344b;font-size:.9rem;`;
const InfoGrid = styled.div`display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.55rem;@media(max-width:560px){grid-template-columns:1fr;}`;
const InfoItem = styled.div`grid-column:${({$wide})=>$wide?'1 / -1':'auto'};padding:.7rem;border-radius:.5rem;background:#f5f7f8;span{display:block;margin-bottom:.2rem;color:#68727d;font-size:.66rem;text-transform:uppercase;}strong{display:block;font-size:.76rem;line-height:1.5;white-space:pre-wrap;overflow-wrap:anywhere;}`;
const MobileTableHint = styled.p`display:none;margin:0 0 .45rem;color:#68727d;font-size:.68rem;@media(max-width:600px){display:block;}@media print{display:none;}`;
const CostTableWrapper = styled.div`width:100%;overflow-x:auto;border:1px solid #dfe4e8;border-radius:.55rem;`;
const CostTable = styled.table`
  width:100%;border-collapse:collapse;font-size:.73rem;min-width:34rem;
  th,td{padding:.58rem;border-bottom:1px solid #dfe4e8;text-align:left;vertical-align:top;}
  th{background:#edf1f3;color:#29465a;font-size:.67rem;text-transform:uppercase;}
  th:nth-child(n+2),td:nth-child(n+2){text-align:right;}
  tbody tr, tfoot tr{break-inside:avoid;}
  tfoot td{font-weight:850;background:#f7f4ef;}
  tfoot .grand-total td{background:#132f46;color:#fff;font-size:.82rem;}
  tfoot .grand-total td:last-child{color:#f1c978;}
  @media(max-width:600px){
    min-width:0;display:block;
    thead{display:none;}tbody,tfoot{display:block;}tr{display:grid;gap:.25rem;padding:.7rem;border-bottom:1px solid #dfe4e8;}td{display:flex;justify-content:space-between;gap:1rem;padding:.18rem 0;border:0!important;text-align:right!important;}td::before{content:attr(data-label);color:#68727d;font-size:.64rem;font-weight:800;text-align:left;text-transform:uppercase;}td:first-child{font-weight:850;}tfoot td[colspan]{display:none;}tfoot tr{background:#f7f4ef;}tfoot .grand-total{background:#132f46;color:#fff;}tfoot .grand-total td::before{color:#dce7ef;}
  }
  @media print{min-width:0;font-size:7.5pt;th,td{padding:5pt;}thead{display:table-header-group;}tbody{display:table-row-group;}tfoot{display:table-footer-group;}tr{display:table-row!important;padding:0!important;}td{display:table-cell!important;padding:5pt!important;}td::before{display:none!important;}}
`;
const TermsGrid = styled.div`display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.55rem;min-width:0;@media(max-width:560px){grid-template-columns:1fr;}`;
const Term = styled.div`min-width:0;display:grid;gap:.18rem;padding:.7rem;border:1px solid #dfe4e8;border-radius:.5rem;break-inside:avoid;overflow-wrap:anywhere;word-break:break-word;strong{font-size:.74rem;}span{min-width:0;color:#68727d;font-size:.69rem;line-height:1.45;overflow-wrap:anywhere;}`;
const LegalLine = styled.p`margin:.55rem 0 0;color:#4f5c66;font-size:.69rem;line-height:1.45;`;
const ElectronicInvoiceNotice = styled.div`
  margin: 1rem 0;
  padding: 0.9rem 1rem;
  border: 1px solid #dbe2e6;
  border-radius: 0.8rem;
  background: #f4f6f7;
  display: grid;
  gap: 0.35rem;
  strong { color: #15344b; font-size: 0.82rem; text-transform: uppercase; letter-spacing: 0.04em; }
  span { color: #5f6c76; font-size: 0.78rem; line-height: 1.5; }
`;

const Acceptance = styled.section`min-width:0;margin-top:1.3rem;padding-top:1rem;border-top:2px solid #c0a375;break-inside:avoid;overflow:hidden;`;
const AcceptanceText = styled.div`min-width:0;strong{font-size:.82rem;}p{max-width:100%;margin:.25rem 0 0;color:#5e6972;font-size:.68rem;line-height:1.5;white-space:normal;overflow-wrap:anywhere;word-break:break-word;}`;
const SignatureGrid = styled.div`display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.8rem;margin-top:.8rem;@media(max-width:560px){grid-template-columns:1fr;}`;
const SignatureBox = styled.div`min-width:0;padding:.65rem;border:1px solid #dfe4e8;border-radius:.5rem;overflow:hidden;span{display:block;max-width:100%;color:#68727d;font-size:.65rem;text-transform:uppercase;overflow-wrap:anywhere;}div{min-height:2.8rem;max-width:100%;margin-top:.35rem;color:#3c4b56;font-size:.7rem;font-style:italic;line-height:1.5;white-space:normal;overflow-wrap:anywhere;word-break:break-word;}`;
const EvidenceDisclosure = styled.p`max-width:100%;margin:.7rem 0 0;color:#68727d;font-size:.64rem;line-height:1.5;overflow-wrap:anywhere;word-break:break-word;`;
const DocumentFooterNote = styled.p`margin:1rem 0 0;padding:.65rem .75rem;border-radius:.55rem;background:#fbf8f2;color:#5f6b75;font-size:.66rem;font-style:italic;line-height:1.5;overflow-wrap:anywhere;`;
const Footer = styled.footer`display:flex;flex-wrap:wrap;gap:.35rem 1rem;min-width:0;margin-top:1.4rem;padding-top:.7rem;border-top:1px solid #dfe4e8;color:#68727d;font-size:.62rem;overflow-wrap:anywhere;strong{color:#17344b;}`;

const EmptyState = styled.main`min-height:70vh;display:grid;place-content:center;text-align:center;padding:2rem;p{max-width:560px;color:#68727d;}button{justify-self:center;border:0;border-radius:999px;padding:.8rem 1.2rem;background:#c0a375;font-weight:800;cursor:pointer;}`;

export default PrintableProjectDocument;
